import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Save, UserPlus, Phone } from 'lucide-react';
import { supabase } from './supabaseClient';
import { getNextPackageNumber } from './utils/packageCounter';
import { useTenant } from './context/TenantContext';
import QuickScanModal from './QuickScanModal';

const WarehouseTerminal = ({ onSubmit, existingCustomers, t, activeManifest, canScan, user }) => {
  const [isNewClient, setIsNewClient] = useState(false);
  const [nextPackageNumber, setNextPackageNumber] = useState(null);
  const [shippingStatus, setShippingStatus] = useState('Send Immediately');
  const [showScanner, setShowScanner] = useState(false);
  const [form, setForm] = useState({
    customerPhone: '', tracking: '', customerId: '', customerName: '', photoFile: null
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const customerIdRef = useRef(null);
  const trackingRef = useRef(null);
  const submitRef = useRef(null);
  const suggestionsRef = useRef(null);
  const { tenant } = useTenant();

  // Client-side prefix filter over the existingCustomers prop.
  // Case-insensitive. Returns [] when input is empty.
  const suggestions = useMemo(() => {
    const query = (form.customerId || '').trim().toUpperCase();
    if (query.length === 0) return [];
    return (existingCustomers || [])
      .filter(c => (c.id || '').toUpperCase().startsWith(query))
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [form.customerId, existingCustomers]);

  // Counter includes both casillero shipments and confirmed encomienda packages.
  // Uses activeManifest if provided (session view), otherwise queries the open manifest.
  const fetchNextPackageNumber = async () => {
    try {
      let manifestId = activeManifest?.id || null;

      if (!manifestId) {
        const { data: openManifest } = await supabase.from('manifests').select('id').eq('status', 'OPEN').maybeSingle();
        manifestId = openManifest?.id || null;
      }

      if (!manifestId) {
        setNextPackageNumber(1);
        return;
      }

      const next = await getNextPackageNumber(supabase, manifestId);
      setNextPackageNumber(next);
    } catch (err) {
      console.error('[Counter] Unexpected error:', err);
      setNextPackageNumber(1);
    }
  };

  /* eslint-disable */
  useEffect(() => {
    if (!tenant) return;
    fetchNextPackageNumber();
  }, [tenant, activeManifest?.id]);
  /* eslint-enable */

  useEffect(() => {
    if (form.customerId && existingCustomers.length > 0) {
      const exists = existingCustomers.some(c => c.id === form.customerId.toUpperCase());
      setIsNewClient(!exists);
    } else {
      setIsNewClient(false);
    }
  }, [form.customerId, existingCustomers]);

  useEffect(() => {
    if (!showSuggestions) return;
    const handleClickOutside = (e) => {
      if (
        customerIdRef.current && !customerIdRef.current.contains(e.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  useEffect(() => {
    if (customerIdRef.current) customerIdRef.current.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    if (isNewClient && !form.customerPhone) {
      alert("El número de teléfono es requerido para clientes nuevos.");
      return;
    }
    if (form.photoFile && form.photoFile.size > 5 * 1024 * 1024) {
      alert('El archivo es muy grande. Máximo 5MB. / File too large. Max 5MB.');
      return;
    }

    const data = {
      ...form,
      weight: null,
      dimensions: null,
      customerId: form.customerId,
      isNewClient,
      serviceType: 'casillero',
      shippingStatus,  // FIX 2: include status in payload
    };

    try {
      await onSubmit(data);
    } catch (err) {
      console.error('[WarehouseTerminal] Submit error:', err);
    }
    fetchNextPackageNumber();
    setForm({ customerPhone: '', tracking: '', customerId: '', customerName: '', photoFile: null });
    setShippingStatus('Send Immediately');
    if (customerIdRef.current) customerIdRef.current.focus();
  };

  return (
    <div className="tile max-w-3xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Package counter banner */}
        {nextPackageNumber !== null && (
          <div className="metric-strip" style={{ gridTemplateColumns: '1fr' }}>
            <div className="metric-card flex items-center justify-between" style={{ background: 'var(--text-main)', borderColor: 'var(--text-main)' }}>
              <span className="metric-label" style={{ color: 'var(--text-tertiary)' }}>PAQUETE A REGISTRAR</span>
              <span className="metric-value" style={{ color: 'var(--blue)', fontSize: '28px', marginTop: 0 }}>#{nextPackageNumber}</span>
            </div>
          </div>
        )}

        {/* Customer ID */}
        <div>
          <label className="metric-label mb-2 block">ID de Cliente</label>
          <div className="relative">
            <input
              ref={customerIdRef}
              className="input font-mono uppercase"
              style={{ fontSize: '16px', padding: '12px' }}
              placeholder="Ingresá el ID..."
              value={form.customerId}
              autoComplete="off"
              onChange={e => {
                setForm({ ...form, customerId: e.target.value.toUpperCase() });
                setShowSuggestions(true);
                setHighlightedIndex(-1);
              }}
              onFocus={() => {
                if (form.customerId.length > 0) setShowSuggestions(true);
              }}
              onKeyDown={e => {
                if (!showSuggestions || suggestions.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex(i => Math.max(i - 1, -1));
                } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                  e.preventDefault();
                  const picked = suggestions[highlightedIndex];
                  setForm(f => ({ ...f, customerId: picked.id }));
                  setShowSuggestions(false);
                  setHighlightedIndex(-1);
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                  setHighlightedIndex(-1);
                }
              }}
            />
            {isNewClient && form.customerId.length > 0 && (
              <span className="absolute right-3 top-2.5 badge badge-ship flex items-center gap-1 animate-pulse">
                <UserPlus size={10} /> CLIENTE NUEVO
              </span>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  background: '#ffffff',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  zIndex: 50,
                }}
              >
                {suggestions.map((c, i) => (
                  <div
                    key={c.id}
                    onMouseDown={e => {
                      e.preventDefault();
                      setForm(f => ({ ...f, customerId: c.id }));
                      setShowSuggestions(false);
                      setHighlightedIndex(-1);
                      if (customerIdRef.current) customerIdRef.current.focus();
                    }}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                      background: highlightedIndex === i ? 'var(--bg)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '14px',
                    }}
                  >
                    <span className="font-mono font-bold" style={{ color: 'var(--avianca-red)', minWidth: '60px' }}>{c.id}</span>
                    <span style={{ color: 'var(--text-main)' }}>{c.full_name || '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* New client phone */}
        {isNewClient && (
          <div className="tile animate-in slide-in-from-top-2" style={{ background: 'var(--blue-soft)', borderColor: 'var(--blue)' }}>
            <label className="metric-label mb-2 flex items-center gap-2" style={{ color: 'var(--blue-text)' }}>
              <Phone size={14} /> Asignar Teléfono (Requerido)
            </label>
            <input
              className="input"
              style={{ background: 'var(--tile)' }}
              placeholder="ej. 555-0000"
              value={form.customerPhone}
              onChange={e => setForm({ ...form, customerPhone: e.target.value })}
              required
            />
          </div>
        )}

        {/* Customer Name */}
        <div>
          <label className="metric-label mb-2 block">Nombre del Cliente</label>
          <input
            className="input"
            placeholder="Escribí el nombre..."
            value={form.customerName}
            onChange={e => setForm({ ...form, customerName: e.target.value })}
          />
        </div>

        {/* Tracking Number */}
        <div>
          <label className="metric-label mb-2 block">Número de Rastreo</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              ref={trackingRef}
              className="input font-mono"
              style={{ flex: 1 }}
              placeholder="Escanear o escribir..."
              value={form.tracking}
              onChange={e => setForm({ ...form, tracking: e.target.value })}
              onKeyDown={e => {
                if ((e.key || '') === 'Enter') {
                  e.preventDefault();
                  if (submitRef.current) submitRef.current.focus();
                }
              }}
            />
            {canScan && (
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="btn btn-secondary"
                style={{
                  padding: '0 16px', height: '46px',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  whiteSpace: 'nowrap', flexShrink: 0,
                  borderColor: 'var(--blue)', color: 'var(--blue)',
                }}
              >
                📷 Escanear
              </button>
            )}
          </div>
        </div>

        {/* QuickScan Modal */}
        {showScanner && (
          <QuickScanModal
            onResult={(text) => {
              setForm(prev => ({ ...prev, tracking: text }));
              setShowScanner(false);
              if (trackingRef.current) trackingRef.current.focus();
            }}
            onClose={() => setShowScanner(false)}
          />
        )}

        {/* SHIP / HOLD toggle — defaults to SHIP */}
        <div className="tile" style={{ background: 'var(--bg)', border: 'none' }}>
          <label className="metric-label block mb-3">
            Estado de Envío / Shipping Status
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShippingStatus('Send Immediately')}
              className={`btn ${shippingStatus === 'Send Immediately' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1, justifyContent: 'center', background: shippingStatus === 'Send Immediately' ? 'var(--green)' : '' }}
            >
              ✈️ SHIP
            </button>
            <button
              type="button"
              onClick={() => setShippingStatus('Postpone to Next Delivery')}
              className={`btn ${shippingStatus === 'Postpone to Next Delivery' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1, justifyContent: 'center', background: shippingStatus === 'Postpone to Next Delivery' ? 'var(--blue)' : '' }}
            >
              ⏸ HOLD
            </button>
          </div>
        </div>

        {/* Photo upload */}
        <div className="tile" style={{ background: 'var(--bg)', border: 'none' }}>
          <label className="metric-label block mb-2">
            Foto del paquete (opcional) / Package photo (optional)
          </label>
          <input
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            className="input"
            style={{ padding: '6px' }}
            onChange={e => setForm({ ...form, photoFile: e.target.files[0] || null })}
            key={form.photoFile === null ? 'reset' : 'set'}
          />
          {form.photoFile && (
            <p className="metric-sub mt-1">
              {form.photoFile.name} ({(form.photoFile.size / 1024).toFixed(0)} KB)
            </p>
          )}
        </div>

        <button
          ref={submitRef}
          type="submit"
          className="btn btn-primary w-full"
          style={{ height: '52px', fontSize: '14px', justifyContent: 'center', background: 'var(--blue)' }}
        >
          <Save size={20} className="mr-2" /> Guardar Paquete
        </button>
      </form>
    </div>
  );
};

export default WarehouseTerminal;
